import { useId, useState, type ChangeEvent, type FormEvent } from 'react';
import { useSWRConfig } from 'swr';

import { useSession } from '../../../app/providers/sessionContext.js';
import { toAppError } from '../../../shared/lib/errors.js';
import { ErrorState } from '../../../shared/ui/ErrorState.js';
import { LoadingState } from '../../../shared/ui/LoadingState.js';
import { changePassword } from '../../auth/data/authRepository.js';
import { updateProfileDisplayName } from '../../identity/data/identityRepository.js';
import { CURRENT_CONTEXT_KEY, useCurrentContext } from '../../identity/hooks/useCurrentContext.js';
import styles from './ProfilePage.module.css';

/**
 * The display name and the password are editable here. Email is the login identifier
 * (changing it is a GoTrue email-confirmation flow, not a profile edit) and the accounting
 * subject name is RPC-only — see `updateProfileDisplayName`.
 */
export function ProfilePage() {
  const session = useSession();
  const state = useCurrentContext();

  if (state.status === 'loading') return <LoadingState label="Đang tải hồ sơ…" />;
  if (state.status === 'error') {
    return <ErrorState message={state.message} onRetry={state.retry} />;
  }

  const savedName = state.context.profile.displayName;

  return (
    <div className={styles.page}>
      <h1>Hồ sơ</h1>

      {/* Keyed on the saved name so the field re-seeds itself when the context refreshes
          after a save — no state-syncing effect needed. */}
      <NameForm key={savedName} savedName={savedName} />

      <section className={styles.card}>
        <p className={styles.readOnlyLabel}>Email đăng nhập</p>
        <p className={styles.readOnlyValue}>
          {session.status === 'signedIn' ? session.session.user.email : '—'}
        </p>
        <p className={styles.hint}>Đổi email chưa làm ở giai đoạn này.</p>
      </section>

      <PasswordForm />
    </div>
  );
}

function NameForm({ savedName }: { savedName: string }) {
  const { mutate } = useSWRConfig();
  const nameId = useId();
  const statusId = useId();

  const [name, setName] = useState(savedName);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const trimmed = name.trim();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (trimmed === '') {
      setStatus('error');
      setMessage('Tên không được để trống.');
      return;
    }

    setStatus('saving');
    setMessage(null);
    try {
      await updateProfileDisplayName(trimmed);
      // Refreshes the shared context, so the header and the greeting pick up the new name
      // without a reload. It also remounts this form via the key above.
      await mutate(CURRENT_CONTEXT_KEY);
    } catch (error) {
      setStatus('error');
      setMessage(toAppError(error).message);
    }
  }

  return (
    <section className={styles.card}>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className={styles.field}>
          <label htmlFor={nameId}>Tên hiển thị</label>
          <input
            id={nameId}
            className={styles.input}
            type="text"
            value={name}
            enterKeyHint="done"
            autoComplete="name"
            aria-describedby={message ? statusId : undefined}
            onChange={(event) => {
              setName(event.target.value);
              setStatus('idle');
              setMessage(null);
            }}
          />
          <p className={styles.hint}>Tên này hiện ở đầu trang và trong lời chào.</p>
        </div>

        {message ? (
          <p className={styles.error} id={statusId} role="alert">
            {message}
          </p>
        ) : null}

        <button
          type="submit"
          className={styles.submit}
          disabled={status === 'saving' || trimmed === savedName}
        >
          {status === 'saving' ? 'Đang lưu…' : 'Lưu thay đổi'}
        </button>
      </form>
    </section>
  );
}

/** Mirrors `minimum_password_length` in supabase/config.toml; the server stays the final check. */
const MIN_PASSWORD_LENGTH = 6;

function passwordProblem(current: string, next: string, confirm: string): string | null {
  if (next.length < MIN_PASSWORD_LENGTH) {
    return `Mật khẩu mới cần ít nhất ${MIN_PASSWORD_LENGTH} ký tự.`;
  }
  if (next === current) return 'Mật khẩu mới phải khác mật khẩu hiện tại.';
  if (next !== confirm) return 'Mật khẩu nhập lại chưa khớp.';
  return null;
}

/**
 * Three fields, native form, no framework (skill 12). The `autoComplete` values are what let
 * a password manager fill the current password and offer a generated new one.
 */
function PasswordForm() {
  const currentId = useId();
  const nextId = useId();
  const confirmId = useId();
  const messageId = useId();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const filled = current !== '' && next !== '' && confirm !== '';

  function edit(setValue: (value: string) => void) {
    return (event: ChangeEvent<HTMLInputElement>) => {
      setValue(event.target.value);
      setStatus('idle');
      setMessage(null);
    };
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const problem = passwordProblem(current, next, confirm);
    if (problem) {
      setStatus('error');
      setMessage(problem);
      return;
    }

    setStatus('saving');
    setMessage(null);
    try {
      await changePassword(current, next);
      setCurrent('');
      setNext('');
      setConfirm('');
      setStatus('saved');
      setMessage('Đã đổi mật khẩu. Lần đăng nhập sau hãy dùng mật khẩu mới.');
    } catch (error) {
      setStatus('error');
      setMessage(toAppError(error).message);
    }
  }

  const describedBy = status === 'error' ? messageId : undefined;

  return (
    <section className={styles.card}>
      <h2 className={styles.cardTitle}>Đổi mật khẩu</h2>
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <div className={styles.field}>
          <label htmlFor={currentId}>Mật khẩu hiện tại</label>
          <input
            id={currentId}
            className={styles.input}
            type="password"
            name="current-password"
            autoComplete="current-password"
            enterKeyHint="next"
            value={current}
            onChange={edit(setCurrent)}
            aria-describedby={describedBy}
          />
        </div>

        <div className={styles.field}>
          <label htmlFor={nextId}>Mật khẩu mới</label>
          <input
            id={nextId}
            className={styles.input}
            type="password"
            name="new-password"
            autoComplete="new-password"
            enterKeyHint="next"
            value={next}
            onChange={edit(setNext)}
            aria-describedby={describedBy}
          />
          <p className={styles.hint}>Ít nhất {MIN_PASSWORD_LENGTH} ký tự.</p>
        </div>

        <div className={styles.field}>
          <label htmlFor={confirmId}>Nhập lại mật khẩu mới</label>
          <input
            id={confirmId}
            className={styles.input}
            type="password"
            name="confirm-password"
            autoComplete="new-password"
            enterKeyHint="done"
            value={confirm}
            onChange={edit(setConfirm)}
            aria-describedby={describedBy}
          />
        </div>

        {message && status === 'saved' ? (
          <p className={styles.success} id={messageId} role="status">
            {message}
          </p>
        ) : null}
        {message && status === 'error' ? (
          <p className={styles.error} id={messageId} role="alert">
            {message}
          </p>
        ) : null}

        <button type="submit" className={styles.submit} disabled={status === 'saving' || !filled}>
          {status === 'saving' ? 'Đang đổi…' : 'Đổi mật khẩu'}
        </button>
      </form>
    </section>
  );
}
